import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { formatCalendarDate } from '../../utils/calendarDate';
import { forWhomLine } from '../../utils/newsFind';
import { track, EVENTS } from '../../services/analytics';
import { FactSection } from './CollegeParts';

// "In the news" on a public college page (#3972, founder pick B of the
// 2026-10-12 placement mocks). Rendered only for a school a story is tagged
// to: the other pages get no section, no "no news yet" line, no empty box.
// It prerenders with the page; the class line reads as the visitor's
// ("Starts with the class of 2028.") until a signed-in student's class is
// known, then turns personal through the same forWhomLine /news uses.

export const SHOWN_ON_PAGE = 2;

export function NewsSourceLine({ entry, surface }) {
    if (!entry.sourceUrl) return entry.sourceLine ? <p className="text-xs text-[#6B6B6B]">Source: {entry.sourceLine}</p> : null;
    return (
        <p className="text-xs text-[#6B6B6B]">
            Source:{' '}
            <a
                href={entry.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track(EVENTS.SOURCE_LINK_CLICKED, { source: 'news_story', surface })}
                className="inline-flex min-h-[44px] items-center gap-0.5 font-medium text-[#1A4D2E] underline underline-offset-2"
            >
                {entry.sourceLine || 'the announcement'}
                <ArrowTopRightOnSquareIcon className="h-3 w-3" aria-hidden="true" />
                <span className="sr-only"> (opens in a new tab)</span>
            </a>
        </p>
    );
}

export default function CollegeNews({ stories, cls = null }) {
    if (!stories || !stories.length) return null;
    const shown = stories.slice(0, SHOWN_ON_PAGE);
    return (
        <FactSection id="in-the-news" title="In the news">
            <ul className="space-y-3" data-testid="college-news">
                {shown.map((e) => {
                    const line = forWhomLine(e, cls);
                    return (
                        <li key={e.slug} data-testid={`college-news-${e.slug}`} className="max-w-3xl rounded-2xl border border-[#E0DED8] bg-white px-4 py-3.5 sm:px-5 sm:py-4">
                            <p className="text-xs text-[#6B6B6B]">
                                <time dateTime={e.date}>{formatCalendarDate(e.date)}</time> · {e.dateLabel}
                            </p>
                            <Link
                                to={`/news/${e.slug}`}
                                onClick={() => track(EVENTS.NEWS_STORY_OPENED, { slug: e.slug, from: 'college_page' })}
                                className="mt-0.5 flex min-h-[44px] items-center text-base sm:text-[17px] font-semibold leading-snug text-[#1A2E1F] hover:text-[#1A4D2E]"
                            >
                                {e.title}
                            </Link>
                            {e.dek && <p className="mt-1 text-sm leading-relaxed text-[#4A4A4A]">{e.dek}</p>}
                            {line.text && (
                                <p data-testid={`college-news-for-${e.slug}`} className={`mt-1 text-[13px] ${line.yours ? 'font-semibold text-[#1F5A33]' : 'text-[#4A4A4A]'}`}>
                                    {line.text}
                                </p>
                            )}
                            <div className="mt-1"><NewsSourceLine entry={e} surface="college-page" /></div>
                        </li>
                    );
                })}
            </ul>
            {stories.length > SHOWN_ON_PAGE && (
                <Link to="/news" data-testid="college-news-all" className="inline-flex min-h-[44px] items-center text-sm font-semibold text-[#1A4D2E] underline underline-offset-2">
                    See all {stories.length} stories on News
                </Link>
            )}
        </FactSection>
    );
}
